'use client';

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, CheckCircle2, Copy, ShieldAlert, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ADMIN_ROUTES, PARTNER_ADMIN_ROUTES } from '@/config/router';
import { useSuperAdmin } from '@/hooks/use-superadmin';
import { trpc } from '@/lib/api/trpc';
import {
  StripePricingGroupPicker,
  isStripePricingPickerValid,
} from '@/components/partners/StripePricingGroupPicker';
import { DomainProvisioning } from '@/components/partners/DomainProvisioning';

/**
 * "Add Partner" wizard — creates the partner DB row and generates
 * the Clerk satellite code snippets.
 *
 * Two outputs:
 *   1. DB row (partners table) — written by `trpc.partners.create`.
 *      Captures slug, mode, subscribed products, partnerDomain config,
 *      optional support email and initial admin invitations.
 *   2. Frontend code snippets — `config/partners.ts` + `brands/<key>.ts`
 *      entries to paste into a PR. Clerk needs these synchronously at
 *      React mount (before any backend round-trip), so they stay
 *      code-level even though the domain config is now also in the DB.
 *
 * Billing deal (CRM/processing revenue split) is configured separately
 * in the Hub UI (Partners → Billing config) — it's a commercial concern
 * that doesn't need to block partner creation.
 */
export default function AddPartnerPage() {
  const navigate = useNavigate();
  const { isSuperAdmin, isLoading: loadingRole } = useSuperAdmin();

  const [brand, setBrand] = React.useState('');
  const [rootDomain, setRootDomain] = React.useState('');
  // Operators enter the root domain + the app sub-domain only; the app
  // host and Clerk FAPI host are derived (app host = "<sub>.<root>",
  // Clerk host = "clerk.<root>") and shown read-only below so nobody has
  // to guess the exact strings.
  const [subdomain, setSubdomain] = React.useState('app');
  const [appName, setAppName] = React.useState('');
  const [internalName, setInternalName] = React.useState('');
  const [primaryColor, setPrimaryColor] = React.useState('#1a73e8');
  const [accentColor, setAccentColor] = React.useState('#0f5bbd');
  const [subscribedProducts, setSubscribedProducts] = React.useState<('crm' | 'payfac')[]>(['crm']);
  const [supportEmail, setSupportEmail] = React.useState('');
  const [adminEmails, setAdminEmails] = React.useState('');
  // ── Stripe sub-account pricing group ──────────────────────────
  // Initialised to `null` ("revert to platform standard") so a new
  // partner with no explicit pick lands on the standard tier. The
  // shared picker (see `StripePricingGroupPicker`) discovers the full
  // list from Stripe + the partners table, so this no longer needs a
  // hand-maintained `standard / bnp / custom` enum.
  const [pricingGroupSelection, setPricingGroupSelection] = React.useState<
    string | null | undefined
  >(null);

  const createMutation = trpc.partners.create.useMutation({
    onSuccess: (result) => {
      toast.success(`Partner "${result.slug}" created`);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  // Derive the app host + Clerk FAPI host from the root domain and the
  // app sub-domain (the only two things operators type).
  const trimmedRoot = rootDomain.trim().toLowerCase();
  const trimmedSub = subdomain.trim().toLowerCase();
  const appHost = trimmedRoot && trimmedSub ? `${trimmedSub}.${trimmedRoot}` : '';
  const clerkFapi = trimmedRoot ? `clerk.${trimmedRoot}` : '';

  const scaffoldInput = {
    brand: brand.trim(),
    rootDomain: trimmedRoot,
    appHost,
    clerkFapi,
    primaryColor,
    accentColor,
    appName: appName.trim() || undefined,
  };

  // All domain fields filled → partner-domain mode + code snippets unlocked.
  const domainComplete =
    scaffoldInput.rootDomain.length > 0 &&
    scaffoldInput.appHost.length > 0 &&
    scaffoldInput.clerkFapi.length > 0;

  // Snippet generation requires brand + all domain fields.
  const complete = scaffoldInput.brand.length > 0 && domainComplete;

  // DB creation requires a valid slug (≥2 chars) and an internal name
  // (the required ops label → partners.name). Domain fields are optional —
  // omitting them creates a reseller-only partner instead.
  const createReady = brand.trim().length >= 2 && internalName.trim().length >= 2;

  const { data: scaffold, error } = trpc.partners.scaffold.useQuery(
    scaffoldInput,
    { enabled: isSuperAdmin && complete, staleTime: 10_000 },
  );

  if (loadingRole) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="mx-auto h-12 w-12 text-muted-foreground" />
          <h2 className="mt-4 text-xl font-semibold">Access Denied</h2>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">
            The Partners panel is restricted to superadmin users.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate(ADMIN_ROUTES.PARTNERS)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to partners
        </Button>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Add a Partner</h1>
          <Badge variant="secondary" className="text-xs">superadmin</Badge>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Onboard a partner onto the shared deploy. We generate the code snippets &amp; the onboarding checklist.
        </p>
      </div>

      <Alert>
        <Sparkles className="h-4 w-4" />
        <AlertTitle>What happens next</AlertTitle>
        <AlertDescription>
          Fill in the form, copy the generated snippets into a PR, and follow the checklist (DNS, Vercel, Clerk satellite).
          No runtime side effects — Clerk satellite config is build-time, so reviews + rollbacks work via git.
        </AlertDescription>
      </Alert>

      <Alert>
        <Sparkles className="h-4 w-4" />
        <AlertTitle>Billing deal lives in the Hub</AlertTitle>
        <AlertDescription>
          This wizard creates the partner DB row and generates the frontend Clerk satellite
          snippet. The billing deal (CRM/processing revenue split) must be configured
          separately in the Hub under <strong>Partners → Billing config</strong>.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Partner details</CardTitle>
          <CardDescription>Brand key is required. Custom domain fields are optional — fill them for <code>partner-domain</code> mode and to generate code snippets.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="brand">Brand key</Label>
              <Input
                id="brand"
                placeholder="acme"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Short lowercase identifier. Used as the hostname → brand key AND as <code>publicMetadata.whitelabel</code>.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="internalName">Internal name</Label>
              <Input
                id="internalName"
                placeholder="Acme Payments"
                value={internalName}
                onChange={(e) => setInternalName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Used internally by Tagada ops.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rootDomain">
                Root domain <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="rootDomain"
                placeholder="acme-example.com"
                value={rootDomain}
                onChange={(e) => setRootDomain(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The partner's apex domain (no <code>https://</code>, no subdomain).
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subdomain">
                App sub-domain <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="subdomain"
                placeholder="app"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Just the label the dashboard runs on (e.g. <code>app</code>), not
                the full host.
              </p>
            </div>
          </div>

          {/* App host + Clerk FAPI host are derived, never typed by hand. */}
          <div className="rounded-md border bg-muted/40 p-3">
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Generated automatically
            </p>
            <dl className="grid gap-3 sm:grid-cols-2 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">App host</dt>
                <dd className="font-mono">{appHost || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Clerk FAPI host</dt>
                <dd className="font-mono">{clerkFapi || '—'}</dd>
              </div>
            </dl>
          </div>

          {trimmedRoot.length >= 2 && trimmedSub.length >= 1 ? (
            <DomainProvisioning rootDomain={trimmedRoot} subdomain={trimmedSub} />
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Branding</CardTitle>
          <CardDescription>Optional — defaults to sensible fallbacks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="appName">Public brand name</Label>
              <Input
                id="appName"
                placeholder="Acme CRM"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Shown to merchants (dashboard, emails, receipts). Defaults to the internal name if empty.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="primaryColor">Primary color</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="primaryColor"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                />
                <div className="h-9 w-9 rounded border" style={{ background: primaryColor }} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="accentColor">Accent color</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="accentColor"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                />
                <div className="h-9 w-9 rounded border" style={{ background: accentColor }} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Create partner record</CardTitle>
          <CardDescription>
            Save the partner row to the database. Mode is set automatically:{' '}
            <code>partner-domain</code> when all domain fields are filled,{' '}
            <code>reseller-only</code> otherwise. Billing deal can be configured later in the Hub.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Subscribed products</Label>
            <div className="flex gap-4">
              {(['crm', 'payfac'] as const).map((product) => (
                <div key={product} className="flex items-center gap-2">
                  <Checkbox
                    id={`product-${product}`}
                    checked={subscribedProducts.includes(product)}
                    onCheckedChange={(checked) =>
                      setSubscribedProducts((prev) =>
                        checked ? [...prev, product] : prev.filter((p) => p !== product),
                      )
                    }
                  />
                  <Label htmlFor={`product-${product}`} className="font-normal">
                    {product}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="supportEmail">Support email <span className="text-muted-foreground">(optional)</span></Label>
            <Input
              id="supportEmail"
              type="email"
              placeholder="support@partner.com"
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>
              Stripe sub-account pricing group{' '}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Stripe Connect <code>payments_pricing</code> group applied when we mint a sub-account for
              one of this partner's merchants. Pick <strong>Standard</strong> unless the partner has a
              negotiated tier with Stripe directly. The list below is discovered live from Stripe + the
              partners table.
            </p>
            <StripePricingGroupPicker
              currentValue={null}
              onChange={setPricingGroupSelection}
              disabled={createMutation.isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="adminEmails">
              Initial admin emails <span className="text-muted-foreground">(optional, one per line)</span>
            </Label>
            <textarea
              id="adminEmails"
              className="flex min-h-[72px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder={"admin@partner.com\nother@partner.com"}
              value={adminEmails}
              onChange={(e) => setAdminEmails(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Each address will receive a Clerk invitation with the <code>partneradmin</code> role.
            </p>
          </div>

          {createMutation.data ? (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Partner created</AlertTitle>
              <AlertDescription className="space-y-2">
                <div>
                  ID: <code className="text-xs">{createMutation.data.id}</code>
                </div>
                {createMutation.data.invited.length > 0 ? (
                  <ul className="text-xs space-y-0.5">
                    {createMutation.data.invited.map((inv) => (
                      <li key={inv.email}>
                        {inv.email}:{' '}
                        {inv.status === 'error'
                          ? `error — ${'error' in inv ? inv.error : ''}`
                          : inv.status}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(PARTNER_ADMIN_ROUTES(createMutation.data!.slug).BASE)}
                >
                  Go to partner page
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          {createMutation.error ? (
            <Alert variant="destructive">
              <AlertTitle>Create failed</AlertTitle>
              <AlertDescription>{createMutation.error.message}</AlertDescription>
            </Alert>
          ) : null}

          <Button
            disabled={
              !createReady ||
              subscribedProducts.length === 0 ||
              createMutation.isPending ||
              !!createMutation.data ||
              !isStripePricingPickerValid(pricingGroupSelection)
            }
            onClick={() => {
              const parsedEmails = adminEmails
                .split('\n')
                .map((e) => e.trim())
                .filter(Boolean);
              // Picker → wire field:
              //   - `null`     → "use platform standard" → omit the
              //                  field so the column stays NULL and
              //                  the resolver falls back at runtime.
              //   - explicit id → persist as the override.
              //   - `undefined` → button is disabled above, never hits.
              const stripePricingGroup =
                pricingGroupSelection === null
                  ? undefined
                  : pricingGroupSelection;
              createMutation.mutate({
                slug: brand.trim(),
                name: internalName.trim(),
                subscribedProducts,
                mode: domainComplete ? 'partner-domain' : 'reseller-only',
                branding: appName.trim() ? { appName: appName.trim() } : undefined,
                supportEmail: supportEmail.trim() || undefined,
                partnerDomain: domainComplete
                  ? {
                      rootDomain: trimmedRoot,
                      appHost,
                      clerkFapi,
                    }
                  : undefined,
                stripePricingGroup,
                adminEmails: parsedEmails.length > 0 ? parsedEmails : undefined,
              });
            }}
          >
            {createMutation.isPending ? 'Creating…' : 'Create partner'}
          </Button>
        </CardContent>
      </Card>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Validation failed</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      ) : null}

      {scaffold ? (
        <>
          <Separator />

          <Card>
            <CardHeader>
              <CardTitle>1. Create the brand file</CardTitle>
              <CardDescription>
                Save as <code>apps/crm-app/src/config/brands/{scaffold.brandKey}.ts</code>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock content={scaffold.brandFileSnippet} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Register the brand</CardTitle>
              <CardDescription>
                Add to <code>PARTNER_CODE_BRANDS</code> in{' '}
                <code>apps/crm-app/src/config/partner-injection.ts</code> (server side; never{' '}
                <code>brands/index.ts</code>, which ships to every partner host)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock content={scaffold.brandsIndexSnippet} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. Register the partner</CardTitle>
              <CardDescription>
                Append to the <code>PARTNERS</code> array in <code>apps/crm-app/src/config/partners.ts</code>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock content={scaffold.partnersFileSnippet} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Onboarding checklist</CardTitle>
              <CardDescription>
                External steps — complete in order. Each is a pre-requisite for the next.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {scaffold.checklist.map((item, idx) => (
                  <li key={idx} className="flex gap-3">
                    <div className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-muted text-xs font-medium">
                      {idx + 1}
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="font-medium">{item.step}</div>
                      <div className="text-sm text-muted-foreground">{item.detail}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
          Fill the required fields above to generate code snippets and onboarding steps.
        </div>
      )}
    </div>
  );
}

function CodeBlock({ content }: { content: string }) {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success('Copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="relative">
      <pre className="max-h-[500px] overflow-auto rounded border bg-muted p-4 text-xs">
        <code>{content}</code>
      </pre>
      <Button
        size="sm"
        variant="outline"
        onClick={onCopy}
        className="absolute right-2 top-2"
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        <span className="ml-1">{copied ? 'Copied' : 'Copy'}</span>
      </Button>
    </div>
  );
}
